import { createApp } from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedState from "pinia-plugin-persistedstate";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";
import Toast, { POSITION } from "vue-toastification";
import "vue-toastification/dist/index.css";
import "./assets/css/toast.css";
import vBlur from "vblur";
import HelpSymbol from "@/components/HelpSymbol.vue";
import middleEllipsis from "./directives/middleEllipsis";

import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faAngleDoubleLeft } from "@fortawesome/free-solid-svg-icons/faAngleDoubleLeft";
import { faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons/faAngleDoubleRight";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons/faAngleDown";
import { faAngleLeft } from "@fortawesome/free-solid-svg-icons/faAngleLeft";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons/faAngleRight";
import { faAngleUp } from "@fortawesome/free-solid-svg-icons/faAngleUp";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons/faArrowDown";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons/faArrowLeft";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons/faArrowRight";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons/faArrowsRotate";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons/faArrowUp";
import { faArrowUpFromBracket } from "@fortawesome/free-solid-svg-icons/faArrowUpFromBracket";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons/faArrowUpRightFromSquare";
import { faAsterisk } from "@fortawesome/free-solid-svg-icons/faAsterisk";
import { faBan } from "@fortawesome/free-solid-svg-icons/faBan";
import { faBarChart } from "@fortawesome/free-solid-svg-icons/faBarChart";
import { faBars } from "@fortawesome/free-solid-svg-icons/faBars";
import { faBolt } from "@fortawesome/free-solid-svg-icons/faBolt";
import { faBroom } from "@fortawesome/free-solid-svg-icons/faBroom";
import { faCakeCandles } from "@fortawesome/free-solid-svg-icons/faCakeCandles";
import { faCalculator } from "@fortawesome/free-solid-svg-icons/faCalculator";
import { faCalendar } from "@fortawesome/free-solid-svg-icons/faCalendar";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { faCaretLeft } from "@fortawesome/free-solid-svg-icons/faCaretLeft";
import { faCaretRight } from "@fortawesome/free-solid-svg-icons/faCaretRight";
import { faCaretUp } from "@fortawesome/free-solid-svg-icons/faCaretUp";
import { faChartArea } from "@fortawesome/free-solid-svg-icons/faChartArea";
import { faChartBar } from "@fortawesome/free-solid-svg-icons/faChartBar";
import { faChartLine } from "@fortawesome/free-solid-svg-icons/faChartLine";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons/faCheckCircle";
import { faCheckSquare } from "@fortawesome/free-solid-svg-icons/faCheckSquare";
import { faCheckToSlot } from "@fortawesome/free-solid-svg-icons/faCheckToSlot";
import { faCircle as fasCircle } from "@fortawesome/free-solid-svg-icons/faCircle";
import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons/faCircleXmark";
import { faClock } from "@fortawesome/free-solid-svg-icons/faClock";
import { faClock as farClock } from "@fortawesome/free-regular-svg-icons/faClock";
import { faCodeCompare } from "@fortawesome/free-solid-svg-icons/faCodeCompare";
import { faCog } from "@fortawesome/free-solid-svg-icons/faCog";
import { faCogs } from "@fortawesome/free-solid-svg-icons/faCogs";
import { faCommentDots } from "@fortawesome/free-solid-svg-icons/faCommentDots";
import { faCompressAlt } from "@fortawesome/free-solid-svg-icons/faCompressAlt";
import { faCopy } from "@fortawesome/free-solid-svg-icons/faCopy";
import { faCreditCard } from "@fortawesome/free-solid-svg-icons/faCreditCard";
import { faAlarmClock } from "@fortawesome/free-solid-svg-icons";
import { faCrosshairs } from "@fortawesome/free-solid-svg-icons/faCrosshairs";
import { faDatabase } from "@fortawesome/free-solid-svg-icons/faDatabase";
import { faDiagramNext } from "@fortawesome/free-solid-svg-icons/faDiagramNext";
import { faDiagramProject } from "@fortawesome/free-solid-svg-icons/faDiagramProject";
import { faDownload } from "@fortawesome/free-solid-svg-icons/faDownload";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons/faEnvelope";
import { faExclamation } from "@fortawesome/free-solid-svg-icons/faExclamation";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons/faExclamationCircle";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons/faExclamationTriangle";
import { faExpand } from "@fortawesome/free-solid-svg-icons";
import { faExpandAlt } from "@fortawesome/free-solid-svg-icons/faExpandAlt";
import { faExternalLinkSquareAlt } from "@fortawesome/free-solid-svg-icons/faExternalLinkSquareAlt";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/free-solid-svg-icons/faEyeSlash";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { faFileSignature } from "@fortawesome/free-solid-svg-icons/faFileSignature";
import { faFillDrip } from "@fortawesome/free-solid-svg-icons/faFillDrip";
import { faFilter } from "@fortawesome/free-solid-svg-icons/faFilter";
import { faFire } from "@fortawesome/free-solid-svg-icons/faFire";
import { faFloppy } from "@/assets/js/faFloppy";
import { faFolderClosed } from "@fortawesome/free-solid-svg-icons/faFolderClosed";
import { faFolderOpen } from "@fortawesome/free-solid-svg-icons/faFolderOpen";
import { faFolderTree } from "@fortawesome/free-solid-svg-icons/faFolderTree";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons/faGripVertical";
import { faHardDrive } from "@fortawesome/free-solid-svg-icons/faHardDrive";
import { faHeart } from "@fortawesome/free-solid-svg-icons/faHeart";
import { faHospital } from "@fortawesome/free-solid-svg-icons/faHospital";
import { faHourglass } from "@fortawesome/free-solid-svg-icons/faHourglass";
import { faHouse } from "@fortawesome/free-solid-svg-icons/faHouse";
import { faImage } from "@fortawesome/free-solid-svg-icons/faImage";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { faLayerGroup } from "@fortawesome/free-solid-svg-icons/faLayerGroup";
import { faLeaf } from "@fortawesome/free-solid-svg-icons/faLeaf";
import { faLink } from "@fortawesome/free-solid-svg-icons/faLink";
import { faLock } from "@fortawesome/free-solid-svg-icons/faLock";
import { faLockOpen } from "@fortawesome/free-solid-svg-icons/faLockOpen";
import { faMagicWandSparkles } from "@fortawesome/free-solid-svg-icons/faMagicWandSparkles";
import { faMars } from "@fortawesome/free-solid-svg-icons/faMars";
import { faMicrophone } from "@fortawesome/free-solid-svg-icons/faMicrophone";
import { faMinus } from "@fortawesome/free-solid-svg-icons/faMinus";
import { faMobileScreen } from "@fortawesome/free-solid-svg-icons/faMobileScreen";
import { faMountainSun } from "@fortawesome/free-solid-svg-icons/faMountainSun";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons/faPaperclip";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons/faPaperPlane";
import { faPause } from "@fortawesome/free-solid-svg-icons/faPause";
import { faPauseCircle } from "@fortawesome/free-solid-svg-icons/faPauseCircle";
import { faPen } from "@fortawesome/free-solid-svg-icons/faPen";
import { faPeopleLine } from "@fortawesome/free-solid-svg-icons/faPeopleLine";
import { faPerson } from "@fortawesome/free-solid-svg-icons/faPerson";
import { faPersonDress } from "@fortawesome/free-solid-svg-icons/faPersonDress";
import { faPlay } from "@fortawesome/free-solid-svg-icons/faPlay";
import { faPlayCircle } from "@fortawesome/free-solid-svg-icons/faPlayCircle";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { faPrint } from "@fortawesome/free-solid-svg-icons/faPrint";
import { faQrcode } from "@fortawesome/free-solid-svg-icons/faQrcode";
import { faQuestion } from "@fortawesome/free-solid-svg-icons/faQuestion";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons/faQuestionCircle";
import { faQuestionCircle as farQuestionEmptyCircle } from "@fortawesome/free-regular-svg-icons/faQuestionCircle";
import { faRecordVinyl } from "@fortawesome/free-solid-svg-icons/faRecordVinyl";
import { faRefresh } from "@fortawesome/free-solid-svg-icons/faRefresh";
import { faSave } from "@fortawesome/free-solid-svg-icons/faSave";
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";
import { faShuffle } from "@fortawesome/free-solid-svg-icons/faShuffle";
import { faSignal } from "@fortawesome/free-solid-svg-icons/faSignal";
import { faSort } from "@fortawesome/free-solid-svg-icons/faSort";
import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";
import { faSquare } from "@fortawesome/free-solid-svg-icons/faSquare";
import { faSquare as farSquare } from "@fortawesome/free-regular-svg-icons/faSquare";
import { faSquareMinus } from "@fortawesome/free-solid-svg-icons/faSquareMinus";
import { faSquarePlus } from "@fortawesome/free-solid-svg-icons/faSquarePlus";
import { faSquareRootVariable } from "@fortawesome/free-solid-svg-icons/faSquareRootVariable";
import { faStar } from "@fortawesome/free-solid-svg-icons/faStar";
import { faStar as farStar } from "@fortawesome/free-regular-svg-icons/faStar";
import { faStop } from "@fortawesome/free-solid-svg-icons/faStop";
import { faTimeline } from "@fortawesome/free-solid-svg-icons/faTimeline";
import { faTable } from "@fortawesome/free-solid-svg-icons/faTable";
import { faTag } from "@fortawesome/free-solid-svg-icons/faTag";
import { faTasks } from "@fortawesome/free-solid-svg-icons/faTasks";
import { faTextWidth } from "@fortawesome/free-solid-svg-icons/faTextWidth";
import { faThumbsDown } from "@fortawesome/free-solid-svg-icons/faThumbsDown";
import { faThumbsUp } from "@fortawesome/free-solid-svg-icons/faThumbsUp";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons/faTimesCircle";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation";
import { faUpload } from "@fortawesome/free-solid-svg-icons/faUpload";
import { faUser } from "@fortawesome/free-solid-svg-icons/faUser";
import { faUserCheck } from "@fortawesome/free-solid-svg-icons/faUserCheck";
import { faUserGroup } from "@fortawesome/free-solid-svg-icons/faUserGroup";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons/faUserPlus";
import { faUsers } from "@fortawesome/free-solid-svg-icons/faUsers";
import { faUsersCog } from "@fortawesome/free-solid-svg-icons/faUsersCog";
import { faVenus } from "@fortawesome/free-solid-svg-icons/faVenus";
import { faX } from "@fortawesome/free-solid-svg-icons/faX";
import { faShareNodes } from "@fortawesome/free-solid-svg-icons/faShareNodes";
import { faSliders } from "@fortawesome/free-solid-svg-icons/faSliders";
import { faVial } from "@fortawesome/free-solid-svg-icons/faVial";
import { faClipboardList } from "@fortawesome/free-solid-svg-icons/faClipboardList";
import { faList } from "@fortawesome/free-solid-svg-icons/faList";
import { faSlidersH } from "@fortawesome/free-solid-svg-icons";

library.add(
  faSlidersH,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleDown,
  faAngleLeft,
  faAngleRight,
  faList,
  faAngleUp,
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowsRotate,
  faArrowUp,
  faArrowUpFromBracket,
  faArrowUpRightFromSquare,
  faAsterisk,
  faBan,
  faBarChart,
  faBars,
  faBolt,
  faBroom,
  faCakeCandles,
  faShareNodes,
  faSliders,
  faVial,
  faClipboardList,
  faCalculator,
  faCalendar,
  faCaretDown,
  faCaretLeft,
  faCaretRight,
  faCaretUp,
  faChartArea,
  faChartBar,
  faChartLine,
  faCheck,
  faCheckCircle,
  faCheckSquare,
  faCheckToSlot,
  faCircleCheck,
  faCircleXmark,
  faClock,
  faCodeCompare,
  faCog,
  faCogs,
  faCommentDots,
  faCompressAlt,
  faCopy,
  faCreditCard,
  faAlarmClock,
  faCrosshairs,
  faDatabase,
  faDiagramNext,
  faDiagramProject,
  faDownload,
  faEdit,
  faEllipsisV,
  faEnvelope,
  faExclamation,
  faExclamationCircle,
  faExclamationTriangle,
  faExpand,
  faExpandAlt,
  faExternalLinkSquareAlt,
  faEye,
  faEyeSlash,
  faFile,
  faFileSignature,
  faFillDrip,
  faFilter,
  faFire,
  faFloppy,
  faFolderClosed,
  faFolderOpen,
  faFolderTree,
  faGear,
  faGripVertical,
  faHardDrive,
  faHeart,
  faHospital,
  faHourglass,
  faHouse,
  faImage,
  faInfoCircle,
  faLayerGroup,
  faLeaf,
  faLink,
  faLock,
  faLockOpen,
  faMagicWandSparkles,
  faMars,
  faMicrophone,
  faMinus,
  faMobileScreen,
  faMountainSun,
  faPaperclip,
  faPaperPlane,
  faPause,
  faPauseCircle,
  faPen,
  faPeopleLine,
  faPerson,
  faPersonDress,
  faPlay,
  faPlayCircle,
  faPlusCircle,
  faPrint,
  faQrcode,
  faTimeline,
  faQuestion,
  faQuestionCircle,
  farClock,
  faRecordVinyl,
  faRefresh,
  farQuestionEmptyCircle,
  farSquare,
  farStar,
  faSave,
  fasCircle,
  faSearch,
  faShuffle,
  faSignal,
  faSort,
  faSpinner,
  faSquare,
  faSquareMinus,
  faSquarePlus,
  faSquareRootVariable,
  faStar,
  faStop,
  faTable,
  faTag,
  faTasks,
  faTextWidth,
  faThumbsDown,
  faThumbsUp,
  faTimes,
  faTimesCircle,
  faTrash,
  faTriangleExclamation,
  faUpload,
  faUser,
  faUserCheck,
  faUserGroup,
  faUserPlus,
  faUsers,
  faUsersCog,
  faVenus,
  faX
);
const app = createApp(App);
const options = {
  // You can set your default options here
  position: POSITION.BOTTOM_CENTER,
  maxToasts: 1,
  toastClassName: "toast-comtainer",
  transition: "TOASTCUSTOM__fade",
};
app.use(Toast, options);
app.directive("middle-ellipsis", middleEllipsis);
app.use(router);
app.use(vBlur);
const pinia = createPinia();
pinia.use(piniaPluginPersistedState);
app.use(pinia);
app.directive("focus", {
  // When the bound element is mounted into the DOM...
  mounted(el) {
    // Focus the element
    el.focus();
  },
});
app.component("font-awesome-icon", FontAwesomeIcon);
app.component("HelpSymbol", HelpSymbol);
app.mount("#app");

// User guiding
const ug_id = import.meta.env.VITE_USERGUIDING;
if (ug_id) {
  (function (g, u, i, d, e, s) {
    g[e] = g[e] || [];
    var f = u.getElementsByTagName(i)[0];
    var k = u.createElement(i);
    k.async = true;
    k.src = "https://static.userguiding.com/media/user-guiding-" + s + "-embedded.js";
    f.parentNode.insertBefore(k, f);
    if (g[d]) return;
    var ug = (g[d] = {
      q: [],
    });
    ug.c = function (n) {
      return function () {
        ug.q.push([n, arguments]);
      };
    };
    var m = ["previewGuide", "finishPreview", "track", "identify", "triggerNps", "hideChecklist", "launchChecklist"];
    for (var j = 0; j < m.length; j += 1) {
      ug[m[j]] = ug.c(m[j]);
    }
  })(window, document, "script", "userGuiding", "userGuidingLayer", ug_id);
}
